import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { FluentProvider } from "@fluentui/react-components";
import { ToastProvider } from "@contexts/ToastContext";
import { WizardProvider } from "@contexts/WizardContext";

import { AuthProvider, useAuth } from "@contexts/AuthContext";
import { ApiProvider } from "@contexts/ApiContext";
import { LoadingProvider } from "@contexts/LoadingContext";
import { ConfigProvider } from "@contexts/ConfigContext";
import { SignalRProvider } from "@contexts/SignalRContext";
import { PushNotificationProvider } from "@contexts/PushNotificationContext";
import { ThemeModeProvider, useThemeMode } from "@contexts/ThemeContext";
import { setAuthTokenGetter } from "@api/apiClient";
import AppRouter from "./router";
import { useEmbedBridge } from "@/embed/useEmbedBridge";
import { useTeamsTab } from "@/embed/useTeamsTab";
import ErrorBoundary from "@components/ErrorBoundary/ErrorBoundary";
import { useApplicationInsights } from "@hooks/useApplicationInsights";
import SessionExpiredDialog from "@features/auth/SessionExpiredDialog/SessionExpiredDialog";
import { getOtlTheme, brandPalette } from "./theme/theme";
import { createDebugLogger } from "@utils/debugLog";

const log = createDebugLogger("app");

// Import SCSS styles
import "./scss/style.scss";

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Inner component to setup API client after auth is initialized
const AppInitializer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { getAccessToken } = useAuth();

  useEffect(() => {
    // Setup token getters for API client
    setAuthTokenGetter(getAccessToken);
  }, [getAccessToken]);

  return <>{children}</>;
};

// Needs both the signed-in user and the region config, so it sits inside ConfigProvider
const TelemetryInitializer: React.FC = () => {
  useApplicationInsights();
  return null;
};

const brandCssVars = {
  "--otl-brand-blue": brandPalette.blue,
} as React.CSSProperties;

const ThemedAppShell: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { themeMode } = useThemeMode();

  // Runs the Teams tab lifecycle, which adopts the Teams theme. It lives here
  // because that needs the theme context. A no-op outside a Teams tab.
  useTeamsTab();

  return (
    <FluentProvider theme={getOtlTheme(themeMode)} style={brandCssVars}>
      {children}
    </FluentProvider>
  );
};

const App: React.FC = () => {
  // Answers the embedding host's handshake. A no-op when running standalone.
  useEmbedBridge();

  useEffect(() => {
    log("React application starting...");
  }, []);

  return (
    <ErrorBoundary>
      <ThemeModeProvider>
        <ThemedAppShell>
          <QueryClientProvider client={queryClient}>
            <BrowserRouter>
              <ApiProvider>
                <AuthProvider>
                  <AppInitializer>
                    <ConfigProvider>
                      <TelemetryInitializer />
                      <LoadingProvider>
                        <SignalRProvider>
                          <PushNotificationProvider>
                            <ToastProvider>
                              <WizardProvider>
                                <AppRouter />
                                <SessionExpiredDialog />
                              </WizardProvider>
                            </ToastProvider>
                          </PushNotificationProvider>
                        </SignalRProvider>
                      </LoadingProvider>
                    </ConfigProvider>
                  </AppInitializer>
                </AuthProvider>
              </ApiProvider>
            </BrowserRouter>
          </QueryClientProvider>
        </ThemedAppShell>
      </ThemeModeProvider>
    </ErrorBoundary>
  );
};

export default App;
